import React, { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../components/AuthContext';

const LoginPage = () => {
    const { login, verifyMfa, isAuthenticated, loading } = useAuth();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);

    // MFA step state
    const [mfaState, setMfaState] = useState(null); // { challengeToken, emailHint, message }
    const [otp, setOtp] = useState('');

    if (isAuthenticated) {
        return <Navigate to="/" replace />;
    }

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setIsLoading(true);

        if (!email.trim()) {
            setError('Email is required');
            setIsLoading(false);
            return;
        }
        if (!password) {
            setError('Password is required');
            setIsLoading(false);
            return;
        }

        const result = await login({ email: email.trim(), password });
        if (result.mfa_required) {
            setMfaState({
                challengeToken: result.mfa_challenge_token,
                emailHint: result.email_hint || '',
                message: result.message || 'Enter the verification code sent to your email',
                devOtp: result.dev_otp || null,
            });
            setOtp(result.dev_otp ? String(result.dev_otp) : '');
        } else if (!result.success) {
            setError(result.error || 'Login failed');
        }
        setIsLoading(false);
    };

    const handleMfaSubmit = async (e) => {
        e.preventDefault();
        setError('');
        const code = otp.trim();
        if (!code) {
            setError('Verification code is required');
            return;
        }
        setIsLoading(true);
        const result = await verifyMfa(mfaState.challengeToken, code);
        if (!result.success) {
            setError(result.error || 'Verification failed');
        }
        setIsLoading(false);
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="spinner"></div>
            </div>
        );
    }

    // ── MFA step ──────────────────────────────────────────────────────────────
    if (mfaState) {
        return (
            <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
                <div className="sm:mx-auto sm:w-full sm:max-w-md">
                    <div className="text-center">
                        <h1 className="text-3xl font-bold text-gray-900">WattSwap</h1>
                        <h2 className="mt-2 text-xl text-gray-600">Firmware Manager</h2>
                    </div>
                </div>
                <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
                    <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
                        <h3 className="text-lg font-semibold text-gray-800 mb-1">Two-step verification</h3>
                        <p className="text-sm text-gray-500 mb-5">
                            {mfaState.message}
                            {mfaState.emailHint ? <><br /><span className="font-medium">{mfaState.emailHint}</span></> : null}
                        </p>
                        {mfaState.devOtp ? (
                            <div className="mb-4 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
                                <span className="font-medium">Dev OTP:</span>{' '}
                                <code className="font-mono tracking-widest text-base">{mfaState.devOtp}</code>
                                <span className="block text-xs text-amber-700 mt-1">Auth is in development mode — email was not sent.</span>
                            </div>
                        ) : null}
                        <form className="space-y-5" onSubmit={handleMfaSubmit}>
                            <div>
                                <label htmlFor="otp-code" className="block text-sm font-medium text-gray-700">
                                    Verification code
                                </label>
                                <div className="mt-1">
                                    <input
                                        id="otp-code"
                                        name="otp"
                                        type="text"
                                        inputMode="numeric"
                                        autoComplete="one-time-code"
                                        maxLength={8}
                                        value={otp}
                                        onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
                                        className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm tracking-widest text-center text-lg font-mono"
                                        placeholder="123456"
                                        autoFocus
                                    />
                                </div>
                            </div>

                            {error && (
                                <div className="bg-red-50 border border-red-200 rounded-md p-4">
                                    <div className="flex">
                                        <div className="text-red-400">
                                            <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                            </svg>
                                        </div>
                                        <div className="ml-3">
                                            <div className="text-sm text-red-700">{error}</div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            <button
                                type="submit"
                                disabled={isLoading}
                                className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {isLoading ? (
                                    <div className="flex items-center">
                                        <div className="spinner mr-2"></div>
                                        Verifying…
                                    </div>
                                ) : (
                                    'Verify'
                                )}
                            </button>
                        </form>

                        <button
                            type="button"
                            onClick={() => { setMfaState(null); setError(''); setOtp(''); }}
                            className="mt-4 w-full text-sm text-center text-primary-600 hover:underline"
                        >
                            ← Back to sign in
                        </button>
                    </div>
                </div>
            </div>
        );
    }
    // ── Password step (default) ───────────────────────────────────────────────

    return (
        <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
            <div className="sm:mx-auto sm:w-full sm:max-w-md">
                <div className="text-center">
                    <h1 className="text-3xl font-bold text-gray-900">WattSwap</h1>
                    <h2 className="mt-2 text-xl text-gray-600">Firmware Manager</h2>
                </div>
            </div>

            <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
                <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
                    <form className="space-y-6" onSubmit={handleSubmit}>
                        <div>
                            <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                                Email
                            </label>
                            <div className="mt-1">
                                <input
                                    id="email"
                                    name="email"
                                    type="email"
                                    autoComplete="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                                    placeholder="you@example.com"
                                />
                            </div>
                        </div>
                        <div>
                            <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                                Password
                            </label>
                            <div className="mt-1">
                                <input
                                    id="password"
                                    name="password"
                                    type="password"
                                    autoComplete="current-password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                                />
                            </div>
                        </div>

                        {error && (
                            <div className="bg-red-50 border border-red-200 rounded-md p-4">
                                <div className="flex">
                                    <div className="text-red-400">
                                        <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                        </svg>
                                    </div>
                                    <div className="ml-3">
                                        <h3 className="text-sm font-medium text-red-800">Authentication Error</h3>
                                        <div className="mt-2 text-sm text-red-700">{error}</div>
                                    </div>
                                </div>
                            </div>
                        )}

                        <div>
                            <button
                                type="submit"
                                disabled={isLoading}
                                className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {isLoading ? (
                                    <div className="flex items-center">
                                        <div className="spinner mr-2"></div>
                                        Signing in...
                                    </div>
                                ) : (
                                    'Sign In'
                                )}
                            </button>
                        </div>
                    </form>

                    <div className="mt-6">
                        <div className="relative">
                            <div className="absolute inset-0 flex items-center">
                                <div className="w-full border-t border-gray-300" />
                            </div>
                            <div className="relative flex justify-center text-sm">
                                <span className="px-2 bg-white text-gray-500">Access Levels</span>
                            </div>
                        </div>
                        <div className="mt-6 space-y-3 text-xs text-gray-600">
                            <div className="flex justify-between">
                                <span className="font-medium">Viewer (Level 1+):</span>
                                <span>View and download firmware</span>
                            </div>
                            <div className="flex justify-between mt-1">
                                <span className="font-medium">Admin (Level 5+):</span>
                                <span>Full firmware management</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default LoginPage;
